'use client';
import React, { useEffect, useState } from 'react';
import { MapPin, Ticket, ArrowRight, Clock, Loader2, Calendar } from 'lucide-react';
import Link from 'next/link';

interface Event {
  id: string;
  title: string;
  type: string;
  date: string;
  day: string;
  month: string;
  time: string;
  venue: string;
  city: string;
  country: string;
  status: string;
  ticketLink: string;
  ticketPrice: string;
  image: string;
  imageAlt: string;
  isPast: boolean;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  CONFIRMED: { label: 'Confirmed', className: 'status-confirmed' },
  UPCOMING: { label: 'Upcoming', className: 'status-scheduled' },
  SOLD_OUT: { label: 'Sold Out', className: 'status-cancelled' },
  PAST: { label: 'Past', className: 'bg-muted/40 text-muted-foreground' },
};

export default function UpcomingEvents() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/events')
      .then((r) => r.json())
      .then((data) => {
        const upcoming = (data.events ?? []).filter((e: Event) => !e.isPast).slice(0, 4);
        setEvents(upcoming);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section id="events" className="py-24 bg-secondary/30">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex justify-center py-16">
          <Loader2 size={32} className="animate-spin text-primary" />
        </div>
      </section>
    );
  }

  return (
    <section id="events" className="py-24 bg-secondary/30">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Live</p>
            <h2 className="text-4xl font-800 text-foreground">Upcoming Events</h2>
          </div>
          <Link href="/events" className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors">
            Full Schedule <ArrowRight size={14} />
          </Link>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Calendar size={40} className="mx-auto mb-4 opacity-40" />
            <p className="text-base font-600 text-foreground mb-2">No upcoming events</p>
            <p className="text-sm">Check back soon for new tour dates.</p>
            <Link href="/booking" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-xl bg-gradient-gold text-primary-foreground text-sm font-700 hover:opacity-90 transition-all">
              Book for Your Event
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {events.map((event, i) => (
              <div key={event.id} className={`group relative rounded-2xl overflow-hidden bg-card border border-border card-glow-hover transition-all duration-300 ${i === 0 ? 'lg:col-span-2' : ''}`}>
                {i === 0 && event.image && (
                  <div className="relative h-48 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent z-10" />
                    <div
                      className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700"
                      style={{ backgroundImage: `url(${event.image})` }}
                      role="img"
                      aria-label={event.imageAlt}
                    />
                    <div className="absolute inset-0 z-20 flex flex-col justify-end p-6">
                      <div className="flex items-start gap-4">
                        <div className="text-center bg-primary/90 rounded-xl px-4 py-3 min-w-[64px]">
                          <p className="text-2xl font-800 text-primary-foreground leading-none">{event.day}</p>
                          <p className="text-[10px] font-600 text-primary-foreground/80 uppercase tracking-wider">{event.month}</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 text-[10px] font-600 rounded-full ${statusConfig[event.status]?.className ?? 'bg-muted text-muted-foreground'}`}>
                              {statusConfig[event.status]?.label ?? event.status}
                            </span>
                            <span className="text-xs text-white/50 uppercase tracking-wider">{event.type}</span>
                          </div>
                          <h3 className="text-xl font-700 text-white mb-1">{event.title}</h3>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-white/60">
                            <span className="flex items-center gap-1"><MapPin size={11} />{event.venue}, {event.city}</span>
                            {event.time && <span className="flex items-center gap-1"><Clock size={11} />{event.time}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-5">
                  {i > 0 && (
                    <div className="flex items-start gap-4 mb-4">
                      <div className="text-center bg-muted/60 rounded-xl px-3 py-2 min-w-[52px]">
                        <p className="text-lg font-800 text-foreground leading-none">{event.day}</p>
                        <p className="text-[9px] font-600 text-muted-foreground uppercase tracking-wider">{event.month}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`px-2 py-0.5 text-[10px] font-600 rounded-full ${statusConfig[event.status]?.className ?? 'bg-muted text-muted-foreground'}`}>
                            {statusConfig[event.status]?.label ?? event.status}
                          </span>
                          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{event.type}</span>
                        </div>
                        <h3 className="text-base font-700 text-foreground truncate">{event.title}</h3>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-4">
                    <span className="flex items-center gap-1.5">
                      <MapPin size={12} className="text-primary" />
                      {event.venue}, {event.city}, {event.country}
                    </span>
                    {event.time && (
                      <span className="flex items-center gap-1.5">
                        <Clock size={12} className="text-primary" />
                        {event.time}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      {event.ticketPrice && (
                        <>
                          <p className="text-[10px] text-muted-foreground">From</p>
                          <p className="text-sm font-700 text-primary font-tabular">{event.ticketPrice}</p>
                        </>
                      )}
                    </div>
                    <Link
                      href={event.ticketLink && event.ticketLink !== '#' ? event.ticketLink : '/booking'}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-gold text-primary-foreground text-xs font-700 hover:opacity-90 active:scale-95 transition-all"
                    >
                      <Ticket size={14} />
                      Get Tickets
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
